import type { CartTicketInfo } from 'context/CartContext/types';
import { normalizeIraqiPhone } from 'utils/iraqiPhone';
import type { FormValues } from '../TicketHolderForm/TicketHolderForm';

type TicketHolderData = {
  sector: string;
  price: number;
  holderPhone: string;
  holderName: string;
  gate: string;
  categoryKey: string;
  category: {
    key: string;
    label: string;
    color: string;
  };
  ticketType?: string;
};

/**
 * Maps validated form values onto the checkout request's per-ticket shape.
 *
 * **Phones leave as E.164** (`+9647XXXXXXXX`), via the app's single normalizer — the field holds
 * national digits beside a static `+964`, so this is where the two are joined, for every source
 * alike. It replaces a hand-rolled `dialingCode + strip-leading-zero` concatenation, which is the
 * class of helper TZ-CO-17 retired.
 *
 * A holder that fails normalization is submitted **unchanged** rather than patched into something
 * plausible: `iraqiPhoneNumberSchema` gates submit, so this branch is unreachable, and if it ever
 * becomes reachable the right outcome is a server-side rejection, not a number we invented that
 * belongs to someone else.
 *
 * `source` and `saveDetails` are deliberately **not** on the wire yet — see `TicketHolderForm`.
 */
export const transformTicketHoldersData = (
  formData: FormValues,
  tickets: CartTicketInfo[],
): TicketHolderData[] => {
  return formData.ticketHolders.map((holder, idx) => {
    const normalizedPhone = normalizeIraqiPhone(holder.phoneNumber);

    return {
      sector: tickets[idx].sector,
      price: tickets[idx].price,
      holderPhone: normalizedPhone.ok ? normalizedPhone.e164 : holder.phoneNumber,
      holderName: holder.name,
      gate: tickets[idx].entrance ? tickets[idx].entrance : 'main',
      categoryKey: tickets[idx].categoryKey,
      category: {
        key: tickets[idx].categoryKey,
        label: tickets[idx].category,
        color: tickets[idx].categoryColor,
      },
      ...(tickets[idx].ticketType && { ticketType: tickets[idx].ticketType }),
    };
  });
};
